import React, { useState } from 'react';
import PDFViewer from '../components/chat/PDFViewer';
import PDFViewButton from '../components/chat/PDFViewButton';

const PDFDemo: React.FC = () => {
  const [pdfUrl, setPdfUrl] = useState<string>('https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf');
  const [inputUrl, setInputUrl] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  // Sample PDF URLs for demonstration
  const samplePdfs = [
    { name: 'W3C Sample PDF', url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf' },
    { name: 'Mozilla PDF.js Sample', url: 'https://raw.githubusercontent.com/mozilla/pdf.js/ba2edeae/web/compressed.tracemonkey-pldi-09.pdf' },
    { name: 'React-PDF Example', url: 'https://react-pdf-example.pdf' },
    { name: 'Sample.pdf (Local)', url: '/sample.pdf' },
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file) {
      setSelectedFile(file);
      // Create a blob URL for the file
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      setPdfUrl(url);
    }
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputUrl.trim()) {
      setPdfUrl(inputUrl);
    }
  };

  const handleSampleClick = (url: string) => {
    setPdfUrl(url);
    setInputUrl(url);
  };

  return (
    <div className="min-h-screen bg-secondary text-white p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-4">PDF Viewer Demo</h1>
        <p className="mb-8">This demo uses React-PDF v9 to display PDFs in your React app as easily as if they were images.</p>

        {/* Controls Section */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">PDF Controls</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* URL Input */}
            <div>
              <h3 className="text-lg font-medium mb-3">Load PDF from URL</h3>
              <form onSubmit={handleUrlSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={inputUrl}
                  onChange={(e) => setInputUrl(e.target.value)}
                  placeholder="Enter PDF URL"
                  className="flex-1 px-4 py-2 bg-gray-700 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <button
                  type="submit"
                  className="bg-accent hover:bg-accent/80 text-white px-4 py-2 rounded-md"
                >
                  Load
                </button>
              </form>
            </div>

            {/* File Upload */}
            <div>
              <h3 className="text-lg font-medium mb-3">Upload PDF File</h3>
              <div className="flex items-center gap-3">
                <label className="flex-1">
                  <div className="bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-md px-4 py-2 cursor-pointer text-center">
                    {selectedFile ? selectedFile.name : 'Choose PDF file'}
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {fileUrl && (
                  <PDFViewButton
                    pdfUrl={fileUrl}
                    buttonText="View"
                    iconOnly
                  />
                )}
              </div>
            </div>
          </div>

          {/* Sample PDFs */}
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-3">Sample PDFs</h3>
            <div className="flex flex-wrap gap-3">
              {samplePdfs.map((pdf, index) => (
                <button
                  key={index}
                  onClick={() => handleSampleClick(pdf.url)}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
                >
                  {pdf.name}
                </button>
              ))}
            </div>
          </div>

          {/* Button Demo */}
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-3">PDF View Button Demo</h3>
            <div className="flex flex-wrap gap-4">
              <PDFViewButton pdfUrl={pdfUrl} />
              <PDFViewButton
                pdfUrl={pdfUrl}
                buttonText="Open Document"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full flex items-center gap-2"
              />
              <PDFViewButton
                pdfUrl={pdfUrl}
                iconOnly
              />
            </div>
          </div>
        </div>

        {/* Embedded Viewer */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Embedded PDF Viewer</h2>
          <div className="bg-gray-900 rounded-lg overflow-hidden" style={{ height: '700px' }}>
            <PDFViewer url={pdfUrl} />
          </div>

          <div className="mt-6 bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-2">React-PDF Implementation</h3>
            <p className="text-sm text-gray-300 mb-2">This component uses:</p>
            <ul className="list-disc pl-5 text-sm text-gray-300 space-y-1">
              <li><code>Document</code> component to load and display the PDF</li>
              <li><code>Page</code> component to render individual pages</li>
              <li>PDF.js worker properly configured for modern browsers</li>
              <li>Support for text layer (text selection) and annotation layer (links)</li>
              <li>Support for non-latin characters through cMaps configuration</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFDemo;
